import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Company } from "./companyType";
import companyApi from "./companyApi";
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { getCompanyCodeFromSessionStorage } from "../lib/global_function";
import { fetchLineOfBusiness } from "../reference/referenceApi";
import { US_STATES } from "../reference/referenceData";
import { validateEmail, validateZipCode, validateAndFormatPhoneNumber } from '../lib/validator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import { Alert } from '../components/ui/alert';
import Spinner from '../components/ui/spinner';

const UpdateCompanyPage: React.FC = () => {
  const breadcrumbs = [
    { label: "Company", path: "/main/company/" },
    { label: "Update", path: "/main/company/update" },
  ];

  const location = useLocation();
  const initialCompany: Company | undefined = location.state?.company;

  const [company, setCompany] = useState<Company | null>(initialCompany || null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<string[]>([]);
  const stateOptions = Array.isArray(US_STATES) ? US_STATES : Object.values(US_STATES);

  useEffect(() => {
    const loadLineOfBusiness = async () => {
      const lineOfBusinessData = await fetchLineOfBusiness();
      setLineOfBusinessOptions(lineOfBusinessData);
    };
    loadLineOfBusiness();
  }, []);

  useEffect(() => {
    if (!initialCompany) {
      const fetchCompany = async () => {
        const companyCode = getCompanyCodeFromSessionStorage(
          process.env.REACT_APP_SESSION_STORAGE_KEY as string
        );
        try {
          const fetchedCompany = await companyApi.getCompanyByCode(companyCode);
          setCompany(fetchedCompany);
        } catch (error) {
          if (error instanceof Error) {
            setErrorMessage(error.message);
          } else {
            setErrorMessage("An unknown error occurred.");
          }
        }
      };

      fetchCompany();
    }
  }, [initialCompany]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!company) return;
    const { name, value } = e.target;
    setCompany({ ...company, [name]: value });
  };

  const handleSave = async () => {
    setLoading(true);
    setErrorMessage("");
    if (!company) return;
    if (!validateAllFields()) {
      setLoading(false);
      return;
    }

    try {
      await companyApi.update(company.company_id, company);
      setMessage('Company has been updated');
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(`${error.message}`);
      } else {
        setErrorMessage('Failed to update company. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];
    setErrorMessage("");

    switch (name) {
      case "zip":
        const zipResult = validateZipCode(value, true);
        if (!zipResult.isValid) {
          errors.push(zipResult.error || "Invalid ZIP code");
        } else {
          setCompany((prev) => prev ? { ...prev, zip: zipResult.value } : prev);
        }
        break;

      case "phone":
        const phoneResult = validateAndFormatPhoneNumber(value);
        if (!phoneResult.isValid) {
          errors.push(phoneResult.error || "Invalid phone number");
        } else {
          setCompany((prev) => prev ? { ...prev, phone: phoneResult.value } : prev);
        }
        break;

      case "email":
        const emailResult = validateEmail(value);
        if (!emailResult.isValid) {
          errors.push(emailResult.error || "Invalid email");
        }
        break;

      default:
        break;
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(", "));
    } else {
      setErrorMessage("");
    }
  };

  const validateAllFields = () => {
    const errors: string[] = [];

    // Check ZIP
    if (company?.zip !== undefined) {
      const zipResult = validateZipCode(company.zip, true);
      if (!zipResult.isValid) {
        errors.push(zipResult.error || 'Invalid ZIP code');
      }
    }

    // Check phone
    if (company?.phone !== undefined) {
      const phoneResult = validateAndFormatPhoneNumber(company.phone);
      if (!phoneResult.isValid) {
        errors.push(phoneResult.error || 'Invalid phone number');
      }
    }

    // Check email
    if (company?.email !== undefined) {
      const emailResult = validateEmail(company.email);
      if (!emailResult.isValid) {
        errors.push(emailResult.error || 'Invalid email');
      }
    }
    setErrorMessage('');

    // If any required fields are undefined, add error
    if (company?.zip === undefined) errors.push('ZIP code is required');
    if (company?.phone === undefined) errors.push('Phone number is required');
    if (company?.email === undefined) errors.push('Email is required');

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
      return false;
    }

    return true;
  };

  if (!company) {
    return (
      <>
        <HorizontalMenu />
        <div className="container mx-auto max-w-7xl px-4 py-6">
          <Breadcrumbs items={breadcrumbs} />
          <Spinner />
        </div>
      </>
    );
  }

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold mb-6">Update Company</h2>

        {message && <Alert variant="success">{message}</Alert>}
        {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

        {!message && (
          <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
          <form className="space-y-3 max-w-2xl">
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="name">Name:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Input
              type="text"
              id="name"
              name="name"
              value={company.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="line_of_business">Line of Business:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Select
              id="line_of_business"
              name="line_of_business"
              value={company.line_of_business}
              onChange={handleChange}
              required
            >
              {lineOfBusinessOptions.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="address_1">Address 1:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Input
              type="text"
              id="address_1"
              name="address_1"
              value={company.address_1}
              onChange={handleChange}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address_2">Address 2:</Label>
            <Input
              type="text"
              id="address_2"
              name="address_2"
              value={company.address_2}
              onChange={handleChange}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="city">City:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Input
              type="text"
              id="city"
              name="city"
              value={company.city}
              onChange={handleChange}
              required
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="state">State:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Select
              id="state"
              name="state"
              value={company.state}
              onChange={handleChange}
              required
            >
              <option value="">Select a State</option>
              {stateOptions.map((state: string, index: number) => (
                <option key={index} value={state}>
                  {state}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="zip">ZIP:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Input
              type="text"
              id="zip"
              name="zip"
              value={company.zip}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="phone">Phone:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Input
              type="text"
              id="phone"
              name="phone"
              value={company.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="email">Email:</Label>
              <span className="text-red-600 font-semibold">*</span>
            </div>
            <Input
              type="email"
              id="email"
              name="email"
              value={company.email}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="web_url">Web URL:</Label>
            <Input
              type="text"
              id="web_url"
              name="web_url"
              value={company.web_url}
              onChange={handleChange}
            />
          </div>

          <div className="pt-2">
            <Button
              type="button"
              onClick={handleSave}
              disabled={loading}
              size="medium"
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
        </div>
        )}
      </div>
    </>
  );
};

export default UpdateCompanyPage;